import { useEffect, useState } from "react"
import { StyledCircle, StyledFilterDrawer } from "../../Transactions/styles"
import {
  CategoriesContainer,
  ContentWrapper,
  PrimaryButton,
  StyledButton,
} from "../styles"
import { getColumns } from "./columns"
import { Box, Button, InputAdornment, Stack, TextField } from "@mui/material"
import { HeaderGrid } from "../../../components/HeaderGrid"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { Search, FilterAltRounded } from "@mui/icons-material"
import CategoryFilters from "../CategoryFilter"
import CloseIcon from "@mui/icons-material/Close"
import { CategoryTable } from "../CategoryTable"
import {
  CategoriesState,
  CategoryFilterType,
  clearFiltersResetFlag,
  fetchCategoryList,
  resetCategoryFilters,
  selectCategories,
  setCategoryFilters,
  setCategoryFormOpen,
} from "../../../slices/categorySlice"
import { useNavigate } from "react-router-dom"
import queryString from "query-string"
import { constructGetCategoryListApiRequestParams } from "../../../utils/categoryHelper"
import AddIcon from "@mui/icons-material/Add"
import CategoryForm from "../AddCategoryForm"
import { debounce } from "lodash"

const Category2 = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [filterOpen, setFilterOpen] = useState(false)
  const drawerWidth = 280
  const category = "Category2"
  const parentCategoryKey = "Category1"

  const { filters, pagination, filterCount, filtersReset, isCategoryFormOpen } =
    useAppSelector((state) => selectCategories(state)[category])

  const fetchCategory = () => {
    const params = queryString.stringify({ ...filters, ...pagination })
    navigate({ search: params })
    dispatch(
      fetchCategoryList(
        constructGetCategoryListApiRequestParams(category, filters, pagination),
      ),
    )
  }

  const fetchParentCategory = (value?: string) => {
    dispatch(
      fetchCategoryList(
        constructGetCategoryListApiRequestParams(
          parentCategoryKey as keyof CategoriesState,
          { search: value || "" },
        ),
      ),
    )
  }

  useEffect(() => {
    if (filtersReset) {
      fetchCategory()
      dispatch(clearFiltersResetFlag({ category }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReset])

  useEffect(() => {
    fetchCategory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination])

  useEffect(() => {
    if (filters.search === null) return
    const debouncedFetch = debounce(() => {
      fetchCategory()
    }, 500)

    debouncedFetch()

    return () => {
      debouncedFetch.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search])

  const handleDrawerClose = () => {
    setFilterOpen(false)
  }

  const ApplyFilterHandler = () => {
    fetchCategory()
    setFilterOpen(false)
  }

  const resetFilterHandler = () => {
    dispatch(resetCategoryFilters({ category }))
    setFilterOpen(false)
  }

  const handleDialogOpen = (row: any) => {
    row && fetchParentCategory()
    dispatch(setCategoryFormOpen({ category, row }))
  }

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newFilters: CategoryFilterType = {
      ...filters,
      search: e.target.value as string,
    }
    dispatch(setCategoryFilters({ category, filters: newFilters }))
  }

  const columns = getColumns(handleDialogOpen)
  return (
    <CategoriesContainer>
      <ContentWrapper isOpen={filterOpen} drawerWidth={drawerWidth}>
        <Stack
          spacing={2}
          style={{ display: "flex", flexGrow: 1, width: "100%" }}
        >
          <HeaderGrid
            rightChildren={
              <PrimaryButton
                variant="contained"
                startIcon={<AddIcon fontSize="small" />}
                onClick={handleDialogOpen}
              >
                Add Category
              </PrimaryButton>
            }
            leftChildren={[
              <TextField
                size="small"
                variant="outlined"
                label="Search by Category"
                placeholder="Search by Category"
                value={filters.search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />,

              <StyledButton
                variant="outlined"
                startIcon={<FilterAltRounded fontSize="small" />}
                endIcon={
                  Boolean(filterCount) && (
                    <StyledCircle>{filterCount}</StyledCircle>
                  )
                }
                color="inherit"
                onClick={() => setFilterOpen(!filterOpen)}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: "100px",
                }}
              >
                Filters
              </StyledButton>,
            ].filter((child) => Boolean(child))}
          />

          <CategoryTable category={category} columns={columns} />
        </Stack>
      </ContentWrapper>
      <StyledFilterDrawer
        variant={filterOpen ? "persistent" : "temporary"}
        anchor="right"
        open={filterOpen}
        sx={{
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
            }}
          >
            <Button color="inherit" onClick={handleDrawerClose}>
              <CloseIcon />
            </Button>
          </Box>
          <CategoryFilters
            resetFilterHandler={resetFilterHandler}
            ApplyFilterHandler={ApplyFilterHandler}
            category={category}
          />
        </Box>
      </StyledFilterDrawer>
      {isCategoryFormOpen && (
        <CategoryForm
          open={isCategoryFormOpen}
          category={category}
          level={2}
          parentCategoryKey={parentCategoryKey}
          parentCategoryLabel="Category"
        />
      )}
    </CategoriesContainer>
  )
}

export default Category2
